
import { WrapResponse } from '@/types';
import axios from 'axios';
import { StateSlice } from '@/stores/appStore';
import { snakeToCamel } from '@/lib/utils';
import { api, host } from '@/lib/env';
import toast from 'react-hot-toast';
import { queryCommonConfig } from './commonSlice';
import { fun } from '@/lib/request';
import dayjs from 'dayjs';
import { useVipRenewalIncentiveRuntimeStore } from '@/stores/vipRenewalIncentiveRuntimeStore';
import {
  captureProductEvent,
  hasExplicitNewUserFlag,
  identifyProductAnalyticsUser,
  resetProductAnalyticsIdentity,
} from '@/lib/analytics/product-events';

export type UserId = number;

// 防止在严格模式或多处并发调用时重复请求 /user/info
let refreshUserInfoLoading = false;
let initialConsumptionsPromise: Promise<void> | null = null;

export type User = {
  userId: UserId;
  avatar: string;
  gender: number;
  mobile: string;
  nickname: string;
  inviteCode: string;
  unionid: string;
  createTime: string;
};

export type UserAssets = {
  balance: number;
  paperStore: {
    total: number;
    used: number;
  };
  paymentCount: number;
}

export type RecoveryOffer = {
  active: false;
} | {
  active: true;
  remainingSeconds: number;
  recoverableAmount: number;
  endTime: number;
};

type RecoveryOfferRow = {
  active?: boolean;
  remainingSeconds?: number;
  recoverableAmount?: number;
  endTime?: number;
};

const inactiveRecoveryOffer: RecoveryOffer = { active: false };

export type FirstOffer = {
  active: false;
} | {
  active: true;
  remainingSeconds: number;
  bonusBeans: number;
  endTime: number;
};

type FirstOfferRow = {
  active?: boolean;
  remainingSeconds?: number;
  bonusBeans?: number;
  endTime?: number;
};

const inactiveFirstOffer: FirstOffer = { active: false };

export type VipIncentiveOfferKind = 'first' | 'recovery';

const normalizeRecoveryOffer = (offer?: RecoveryOfferRow | null): RecoveryOffer => {
  if (!offer?.active) {
    return inactiveRecoveryOffer;
  }

  const remainingSeconds = Number(offer.remainingSeconds);
  const recoverableAmount = Number(offer.recoverableAmount);
  const endTime = Number(offer.endTime);

  if (
    !Number.isFinite(remainingSeconds)
    || remainingSeconds <= 0
    || !Number.isFinite(recoverableAmount)
    || recoverableAmount <= 0
    || !Number.isFinite(endTime)
    || endTime <= 0
  ) {
    return inactiveRecoveryOffer;
  }

  return {
    active: true,
    remainingSeconds,
    recoverableAmount,
    endTime,
  };
};

const normalizeFirstOffer = (offer?: FirstOfferRow | null): FirstOffer => {
  if (!offer?.active) {
    return inactiveFirstOffer;
  }

  const remainingSeconds = Number(offer.remainingSeconds);
  const bonusBeans = Number(offer.bonusBeans);
  const endTime = Number(offer.endTime);

  if (
    !Number.isFinite(remainingSeconds)
    || remainingSeconds <= 0
    || !Number.isFinite(bonusBeans)
    || bonusBeans <= 0
    || !Number.isFinite(endTime)
    || endTime <= 0
  ) {
    return inactiveFirstOffer;
  }

  return {
    active: true,
    remainingSeconds,
    bonusBeans,
    endTime,
  };
};

/** 与 /user/info 的 vip_type 一致；非会员为空串 */
export type UserVipType = '' | 'plus' | 'plus_3x';

export type UserVip = {
  /** 0 未开通 1 已开通会员（不含档位，档位见 vipType） */
  vip: 0 | 1;
  vipType: UserVipType;
  vipExtra: {
    // 过期时间
    expireTime: string;
    // 更新类型 auto 自动续费
    renewalType: 'auto' | 'none';
    // 激活时间
    activateTime: string;
    // 权益刷新时间
    rightsUpdateTime: string;
    /** 接口可能在 vip_extra 内重复返回会员类型 */
    vipType?: UserVipType;
  };
  limits: {
    // 自定义 AI 列限制
    customLimit: number;
    // 表格展示 AI 列限制
    aiColLimit: number;
  };
  remainingDays: number;
}

export type VipUpgradePreviewOptionStatus = 'normal' | 'exceeded';

export type VipUpgradePreviewOption = {
  price: number;
  duration: string;
  deduction: number;
  actualAmount: number;
  status: VipUpgradePreviewOptionStatus;
};

/** /user/vip/upgradePreview 有数据时（仅 Plus 可升级至 3 倍） */
export type VipUpgradePreviewData = {
  currentVipType: string;
  remainingDays: number;
  remainingMonths: number;
  monthlyPrice: number;
  beansBalance: number;
  beansMonths: number;
  totalDeduction: number;
  upgradeOptions: VipUpgradePreviewOption[];
};

type LoginParams = {
  mobile: string;
  smsCode: string;
  inviteCode?: string;
}

export type Balance = {
  money: number;
  refund: number;
  createTime: string;
  desc: string;
  extra: {
    expireTime: number;
    expired: boolean;
    used: number;
    orderNo: string;
    orderInfo: {
      productName: string;
      status: 'paid' | 'refund';
      money: number;
      payChannel: 'wechat';
      payTime: string;
    },
    workflowId?: string;
  };
  extraStr: string;
  status: 'freeze' | 'finish' | 'rollback';
}

export type BalanceType = 'consume' | 'add' | 'order';

export type Balances = {
  total: number;
  pageNumber: number;
  pageSize: number;
  list: Balance[];
}

export type Consumptions = Record<string, {
  price: number;
  // 优惠前价格
  priceBeforeDiscount?: number;
  label: string;
  desc: string;
  unit?: string;
  consume?: boolean;
  bit?: {
    count: number;
    unit: string;
    label: string;
  },
  firstDiscount?: number;
}>;


export type FamousQuote = {
  name: string;
  url: string;
};
export type FamousQuotes = {
  mode: 'order' | 'random',
  value: {
    name: string;
    url: string;
  }[],
};

export type ActivitiesStat = {
  days: number;
  peoples: number;
  rewards: number;
}

export type DailyTask = {
  chat: 0 | 1;
  note: 0 | 1;
  uploadPaper: 0 | 1;
  received: 0 | 1; // 是否已领取奖励
}

export type DailyCalendarParams = {
  sDate?: string;
  eDate?: string;
};
export type DailyCalendar = {
  currDate: string;
  cal: string[];
};

export type CouponStatus = 'unused' | 'used' | 'expired' | '';
export type GetCouponListParams = {
  scene?: 'summary' | 'paper_translate';
  status?: CouponStatus;
}
export type CouponItem = {
  couponId: number;
  couponName: string;
  couponCode: string;
  discountType: string;
  discountValue: number;
  status: CouponStatus;
  startTime: string;
  endTime: string;
}

export type UserSlice = {
  user: User | null;
  userQuotas: UserAssets | null;
  userVip: UserVip | null;
  recoveryOffer: RecoveryOffer;
  firstOffer: FirstOffer;
  /** Plus →3x 升级预览；非 Plus 或未登录时接口返回空数组，此处为 null */
  vipUpgradePreview: VipUpgradePreviewData | null;
  vipUpgradePreviewLoading: boolean;
  fetchVipUpgradePreview: () => Promise<VipUpgradePreviewData | null>;
  loginLoading: boolean;
  checkLogin: () => Promise<boolean>;
  checkWechatLogin: (code: string, appid: string, debug?: boolean) => Promise<User | null>;
  login: (params: LoginParams) => Promise<boolean>;
  logout: () => Promise<boolean>;
  renameUser: (nickname: string) => Promise<boolean>;
  refreshUserInfo: (force?: boolean) => Promise<{
    user: User | null;
    userQuotas: UserAssets | null;
    userVip: UserVip | null;
    recoveryOffer: RecoveryOffer;
    firstOffer: FirstOffer;
  } | null>;
  expireVipIncentiveOffer: (kind?: VipIncentiveOfferKind) => void;
  fetchBalancesLoading: boolean;
  fetchBalances: (o: {
    type: BalanceType;
    page: number;
  }) => Promise<Balances | null>;
  consumptions: Consumptions | null;
  initialConsumptions: () => Promise<void>;
  initialConsumptionsLoading: boolean;
  // 余额不足提醒，boolean表示是否需要飞豆消耗，数字表示需要消耗的飞豆数量。0表示不需要消耗飞豆，仅弹出提醒
  // ⚠️ 废弃 请使用 paymentSlice 中的 paymentRecharge
  balanceInsufficient: number | boolean;
  // ⚠️ 废弃 请使用 paymentSlice 中的 openPaymentRecharge
  setBalanceInsufficient: (balanceInsufficient: number | boolean) => void;
  famousQuotes: FamousQuotes | null;
  initialFamousQuotes: () => Promise<void>;
  activities: {
    // 活动统计数据
    stat: ActivitiesStat | null;
    fetchStat: () => Promise<void>;
    // 日常活动完成情况
    dailyTask: DailyTask | null;
    fetchDailyTask: () => Promise<void>;
    // 领取日常任务奖励
    receiveDailyPrize: () => Promise<void>;
    receiving: boolean;
    // 日常活动日历，只返回有签到的日期
    dailyCalendar: DailyCalendar | null;
    fetchDailyCalendar: () => Promise<void>;
  };
  unbindWechat: () => Promise<boolean>;
  bindWechat: (code: string, appid: string, debug?: boolean) => Promise<User | null>;
  //  /user/coupon/list
  getCouponList: (params?: GetCouponListParams) => Promise<CouponItem[]>
}

export const createUserSlice: StateSlice<UserSlice> = ((set, get) => ({
  user: null,
  userQuotas: null,
  userVip: null,
  recoveryOffer: inactiveRecoveryOffer,
  firstOffer: inactiveFirstOffer,
  vipUpgradePreview: null,
  vipUpgradePreviewLoading: false,
  loginLoading: false,
  checkLogin: async () => {
    try {
      set((state) => {
        state.loginLoading = true;
      });
      const { data, code } = (await fun.post<WrapResponse<User>>(api('/user/checklogin', true))).data;
      if (code === 0) {
        set((state) => {
          state.user = snakeToCamel<User>(data);
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking login status:', error);
      return false;
    } finally {
      set((state) => {
        state.loginLoading = false;
      });
    }
  },
  checkWechatLogin: async (code, appid, debug = false) => {
    try {
      set((state) => {
        state.loginLoading = true;
      });
      const url = api(`/oauth/wechat/login?code=${code}&app_id=${appid}`, true);
      const data = (await fun.get<WrapResponse<User>>(url)).data;
      debug && alert(JSON.stringify(data));
      if (data.code === 0) {
        await get().refreshUserInfo();
        const user = get().user;
        if (user?.userId) {
          identifyProductAnalyticsUser(user.userId);
          captureProductEvent('login_succeeded', { auth_method: 'wechat_oauth' });
          if (hasExplicitNewUserFlag(data)) {
            captureProductEvent('signup_completed', { auth_method: 'wechat_oauth' });
          }
        }
        return user;
      }
      return null;
    } catch (error) {
      console.error('Error checking wx login:', error);
      return null;
    } finally {
      set((state) => {
        state.loginLoading = false;
      });
    }
  },
  login: async ({ mobile, smsCode, inviteCode }) => {
    try {
      set((state) => {
        state.loginLoading = true;
      });
      const formData = new FormData();
      formData.append('username', mobile);
      formData.append('sms_code', smsCode);
      if (inviteCode) {
        formData.append('invite_code', inviteCode);
      }
      const {
        data,
        code,
      } = (await fun.post<WrapResponse<User>>(
        api('/user/login', true),
        formData,
      )).data;
      if (code === 0) {
        const user = snakeToCamel<User>(data);
        set((state) => {
          state.user = user;
        });
        identifyProductAnalyticsUser(user.userId);
        captureProductEvent('login_succeeded', { auth_method: 'phone_sms' });
        if (hasExplicitNewUserFlag(data)) {
          captureProductEvent('signup_completed', { auth_method: 'phone_sms' });
        }
        return true;
      }
      return false;
    } catch (error) {
      return false;
    } finally {
      set((state) => {
        state.loginLoading = false;
      });
    }
  },
  logout: async () => {
    try {
      set((state) => {
        state.loginLoading = true;
      });
      const { code } = (await fun.post(
        api('/user/logout', true)
      )).data;
      if (code === 0) {
        set((state) => {
          state.user = null;
          state.userQuotas = null;
          state.userVip = null;
          state.recoveryOffer = inactiveRecoveryOffer;
          state.firstOffer = inactiveFirstOffer;
          state.vipUpgradePreview = null;
        });
        resetProductAnalyticsIdentity();
        return true;
      }
      return false;
    } catch (error) {
      return false;
    } finally {
      set((state) => {
        state.loginLoading = false;
      });
    }
  },
  renameUser: async (nickname) => {
    const user = get().user;
    if (!user) {
      return false;
    }
    try {
      const formData = new FormData();
      formData.append('nickname', nickname);
      const responseData = (await fun.post<WrapResponse<{}>>(
        api('/user/edit/rename', true),
        formData,
      )).data;
      if (responseData.code === 0) {
        set((state) => {
          state.user!.nickname = nickname;
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error rename user:', error);
      return false;
    } finally {

    }
  },
  refreshUserInfo: async (force = false) => {
    if (refreshUserInfoLoading && !force) {
      return null;
    }
    refreshUserInfoLoading = true;
    try {
      const { data, code } = (
        await fun.post<WrapResponse<Record<string, unknown>>>(
          api('/user/info', true),
          force ? {} : { refresh: 1 }
        )
      ).data;
      if (code === 0) {
        set((state) => {
          const row = snakeToCamel(data as never) as User & UserAssets & {
            vip: number;
            vipType?: string;
            vipExtra: UserVip['vipExtra'];
            limits: UserVip['limits'];
            recoveryOffer?: RecoveryOfferRow;
            firstOffer?: FirstOfferRow;
          };
          const {
            balance,
            paperStore,
            paymentCount,
            vip: rawVip,
            vipType: apiVipType,
            vipExtra,
            limits,
            recoveryOffer,
            firstOffer,
            ...user
          } = row;
          const normalizedRecoveryOffer = normalizeRecoveryOffer(recoveryOffer);
          const normalizedFirstOffer = normalizeFirstOffer(firstOffer);
          state.userQuotas = {
            balance,
            paperStore,
            paymentCount,
          };
          const remainingDays = dayjs(vipExtra.expireTime).diff(dayjs(), 'day');
          let vip: 0 | 1 = rawVip === 1 || rawVip === 2 ? 1 : 0;
          let vipType: UserVipType = '';
          if (rawVip === 2) {
            vipType = 'plus_3x';
          } else if (apiVipType === 'plus_3x' || apiVipType === 'plus') {
            vipType = apiVipType;
          } else if (vipExtra?.vipType === 'plus_3x' || vipExtra?.vipType === 'plus') {
            vipType = vipExtra.vipType;
          } else if (vip === 1) {
            vipType = 'plus';
          }
          if (vip === 0) {
            vipType = '';
          }
          state.userVip = {
            vip,
            vipType,
            vipExtra,
            limits,
            remainingDays,
          };
          state.recoveryOffer = normalizedRecoveryOffer;
          state.firstOffer = normalizedFirstOffer;
          state.user = user;
        });
        const {
          firstOffer: latestFirstOffer,
          recoveryOffer: latestRecoveryOffer,
        } = get();
        const { clearDismissedEndTimes } = useVipRenewalIncentiveRuntimeStore.getState();
        if (!latestFirstOffer.active) {
          clearDismissedEndTimes('first');
        }
        if (!latestRecoveryOffer.active) {
          clearDismissedEndTimes('recovery');
        }
        return {
          user: get().user,
          userQuotas: get().userQuotas,
          userVip: get().userVip,
          recoveryOffer: get().recoveryOffer,
          firstOffer: get().firstOffer,
        };
      } else if (code === 6010) {
        // 用户未绑定手机号
        set((state) => {
          state.user = {
            ...get().user,
          } as User;
        });
        return {
          user: get().user,
          userQuotas: get().userQuotas,
          userVip: get().userVip,
          recoveryOffer: get().recoveryOffer,
          firstOffer: get().firstOffer,
        };
      }
      return null;
    } catch (error) {
      console.error('Error get user info:', error);
      return null;
    } finally {
      refreshUserInfoLoading = false;
    }
  },
  expireVipIncentiveOffer: (kind) => {
    set((state) => {
      if (!kind || kind === 'recovery') {
        state.recoveryOffer = inactiveRecoveryOffer;
      }
      if (!kind || kind === 'first') {
        state.firstOffer = inactiveFirstOffer;
      }
    });
  },
  fetchBalancesLoading: false,
  fetchBalances: async ({
    type,
    page = 0,
  }) => {
    try {
      set((state) => {
        state.fetchBalancesLoading = true;
      });
      const formData = new FormData();
      formData.append('type', type);
      formData.append('page', page.toString());
      const responseData = (await fun.post<WrapResponse<Balances>>(
        api('/user/moneyLog', true),
        formData,
      )).data;
      if (responseData.code === 0) {
        return snakeToCamel<Balances>(responseData.data);
      }
      return null;
    } catch (error) {
      console.error('Error rename user:', error);
      return null;
    } finally {
      set((state) => {
        state.fetchBalancesLoading = false;
      });
    }
  },
  consumptions: null,
  initialConsumptionsLoading: false,
  initialConsumptions: () => {
    if (get().consumptions) {
      return Promise.resolve();
    }
    if (initialConsumptionsPromise) {
      return initialConsumptionsPromise;
    }

    set((state) => {
      state.initialConsumptionsLoading = true;
    });
    const request = (async () => {
      try {
        const responseData = await queryCommonConfig<Consumptions>('scene_price');
        if (responseData?.code === 0) {
          set(state => {
            state.consumptions = snakeToCamel<Consumptions>(responseData.data);
          });
        }
      } catch (error) {
        console.error('Error query common/config:', error);
      } finally {
        set((state) => {
          state.initialConsumptionsLoading = false;
        });
      }
    })();
    initialConsumptionsPromise = request.finally(() => {
      initialConsumptionsPromise = null;
    });
    return initialConsumptionsPromise;
  },
  // 需要消耗的飞豆数量，或者true/false表示需要飞豆消耗
  //  ⚠️ 废弃 请使用 paymentSlice 中的 paymentRecharge
  balanceInsufficient: 0,
  // ⚠️ 废弃 请使用 paymentSlice 中的 openPaymentRecharge
  setBalanceInsufficient: (insufficient) => {
    // set(state => {
    //   const paymentOpen = typeof insufficient === 'boolean' ? insufficient : insufficient >= 0;
    //   state.payment.paymentOpen = paymentOpen;
    //   state.payment.paymentType = 'recharge';
    //   state.balanceInsufficient = insufficient;
    // });
    get().payment.openPaymentRecharge(insufficient);
  },
  famousQuotes: null,
  initialFamousQuotes: async () => {
    if (get().famousQuotes) {
      return;
    }
    try {
      const formData = new FormData();
      formData.append('key', 'famous_quotes');
      const responseData = (await fun.post<WrapResponse<FamousQuotes>>(
        api('/common/config', true),
        formData,
      )).data;
      if (responseData.code === 0) {
        set(state => {
          state.famousQuotes = snakeToCamel<FamousQuotes>(responseData.data);
        });
      }
    } catch (error) {
      console.error('Error query common/config:', error);
    } finally {

    }
  },
  activities: {
    stat: null,
    fetchStat: async () => {
      try {
        const responseData = (await fun.post<WrapResponse<ActivitiesStat>>(
          api('/activity/stat', true),
        )).data;
        if (responseData.code === 0) {
          set(state => {
            state.activities.stat = snakeToCamel<ActivitiesStat>(responseData.data);
          });
        }
      } catch (error) {
        console.error('Error query activity/stat:', error);
      } finally {

      }
    },
    dailyTask: null,
    fetchDailyTask: async () => {
      try {
        const responseData = (await fun.post<WrapResponse<DailyTask>>(
          api('/activity/dailyTask', true),
        )).data;
        if (responseData.code === 0) {
          set(state => {
            state.activities.dailyTask = snakeToCamel<DailyTask>(responseData.data);
          });
        }
      } catch (error) {
        console.error('Error query activity/dailyTask:', error);
      } finally {

      }
    },
    receiving: false,
    receiveDailyPrize: async () => {
      const toastId = 'receiveDailyPrize';
      if (get().activities.receiving) {
        return;
      }
      set(state => {
        state.activities.receiving = true;
      });
      toast.success('奖励领取中...', {
        id: toastId
      });
      try {
        const responseData = (await fun.post<WrapResponse<ActivitiesStat>>(
          api('/activity/dailyPrizeReceive', true),
        )).data;
        if (responseData.code === 0) {
          get().activities.fetchDailyTask();
          get().activities.fetchStat();
          get().activities.fetchDailyCalendar();
          toast.success('奖励领取成功', {
            id: toastId
          });
        } else {
          toast.error(responseData.message, {
            id: toastId
          });
        }
      } catch (error) {
        console.error('Error query activity/dailyPrizeReceive:', error);
      } finally {
        set(state => {
          state.activities.receiving = false;
        });
        toast.dismiss(toastId);
      }
    },
    dailyCalendar: null,
    fetchDailyCalendar: async () => {
      try {
        const responseData = (await fun.post<WrapResponse<DailyCalendar>>(
          api('/activity/dailyCal', true),
        )).data;
        if (responseData.code === 0) {
          set(state => {
            state.activities.dailyCalendar = snakeToCamel<DailyCalendar>(
              responseData.data
            );
          });
        }
      } catch (error) {
        console.error('Error query activity/dailyCal:', error);
      } finally {

      }
    },

  },
  unbindWechat: async () => {
    const user = get().user;
    if (!user) {
      return false;
    }
    try {
      const responseData = (await fun.post<WrapResponse<{}>>(
        api('/user/unbind', true),
        {
          channel: 'wechat',
        },
      )).data;
      if (responseData.code === 0) {
        get().refreshUserInfo();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error unbind wechat:', error);
      return false;
    } finally {

    }
  },
  bindWechat: async (code, appid, debug = false) => {
    try {
      const url = api(`/user/bind?code=${code}&app_id=${appid}`, true);
      const data = (await axios.get<WrapResponse<User>>(url)).data;
      debug && alert(JSON.stringify(data));
      if (data.code === 0) {
        await get().refreshUserInfo();
        return get().user;
      }
      return null;
    } catch (error) {
      console.error('Error checking wx bind:', error);
      return null;
    } finally {
    }
  },
  fetchVipUpgradePreview: async () => {
    if (get().vipUpgradePreviewLoading) {
      return get().vipUpgradePreview;
    }
    set((state) => {
      state.vipUpgradePreviewLoading = true;
    });
    try {
      const { data, code, message } = (
        await fun.post<WrapResponse<Record<string, unknown> | unknown[]>>(
          api('/user/vip/upgradePreview', true),
        )
      ).data;
      if (code !== 0) {
        if (message) toast.error(message);
        return null;
      }
      if (Array.isArray(data)) {
        set((state) => {
          state.vipUpgradePreview = null;
        });
        return null;
      }
      const preview = snakeToCamel(data as never) as VipUpgradePreviewData;
      set((state) => {
        state.vipUpgradePreview = preview;
      });
      return preview;
    } catch (error) {
      console.error('Error fetchVipUpgradePreview:', error);
      return null;
    } finally {
      set((state) => {
        state.vipUpgradePreviewLoading = false;
      });
    }
  },
  getCouponList: async (params) => {
    const {
      scene = 'summary',
      status = 'unused',
    } = params || {};
    try {
      const { code, data, message } = (await fun.post<WrapResponse<CouponItem[]>>(
        api('/user/coupon/list', true),
        {
          scene,
          status,
        }
      )).data;
      if (code === 0) {
        const items = snakeToCamel<CouponItem[]>(data);
        return items.map((item) => ({
          ...item,
          discountValue: Number(item.discountValue),
        }))
      } else {
        toast.error(message);
      }
      return [];
    } catch (error) {
      console.error('Error receive PaymentVip:', error);
      return [];
    }
  }
}));
